import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import ApiService from '../services/ApiService';
import { obtenerProductoPorId } from '../services/productoService';
import { generateMetaPurchaseEventId, generateMetaPurchaseEventTime } from '../utils/metaPurchaseEventId';
import {
  META_PURCHASE_EVENT_NAME,
  META_CUSTOMER_TYPE,
} from '../constants/metaPixel';
import { setMpCheckoutPedidoId } from '../utils/mpCheckoutSession';

const CarritoContext = createContext();

const toNumber = (val) => {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
};

const aplicarPreciosEnItems = (itemsOrigen, preciosPorId) => {
  const eliminados = [];
  let preciosActualizados = false;

  const items = itemsOrigen.flatMap((item) => {
    const actual = preciosPorId.get(String(item.id));
    if (!actual) {
      eliminados.push(item);
      return [];
    }

    const precio = toNumber(actual.precio);

    if (precio !== toNumber(item.precio)) {
      preciosActualizados = true;
    }

    return [{
      ...item,
      precio,
      nombre: actual.nombre || item.nombre,
      imagenURL: actual.imagenURL || item.imagenURL,
    }];
  });

  return {
    items,
    preciosActualizados: preciosActualizados || eliminados.length > 0,
    eliminados,
  };
};

export const useCarrito = () => {
  const context = useContext(CarritoContext);
  if (!context) {
    throw new Error('useCarrito debe ser usado dentro de un CarritoProvider');
  }
  return context;
};

export const CarritoProvider = ({ children }) => {
  const [carritoItems, setCarritoItems] = useState(() => {
    const savedCarrito = localStorage.getItem('carrito');
    return savedCarrito ? JSON.parse(savedCarrito) : [];
  });
  const carritoItemsRef = useRef(carritoItems);
  carritoItemsRef.current = carritoItems;

  const [showCarritoSidebar, setShowCarritoSidebar] = useState(false);

  useEffect(() => {
    localStorage.setItem('carrito', JSON.stringify(carritoItems));
  }, [carritoItems]);

  const agregarAlCarrito = (producto, cantidad = 1) => {
    const qty = Math.max(1, Math.min(999, Number(cantidad) || 1));

    setCarritoItems((prev) => {
      const existente = prev.find((item) => item.id === producto.id);

      if (existente) {
        return prev.map((item) =>
          item.id === producto.id
            ? {
                ...item,
                cantidad: item.cantidad + qty,
                precio: toNumber(producto.precio),
                nombre: producto.nombre || item.nombre,
                imagenURL: producto.imagenURL || item.imagenURL,
              }
            : item
        );
      }

      return [
        ...prev,
        {
          ...producto,
          precio: toNumber(producto.precio),
          cantidad: qty,
        },
      ];
    });
    setShowCarritoSidebar(true);
  };

  const incrementarCantidad = (id) => {
    setCarritoItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, cantidad: item.cantidad + 1 } : item
      )
    );
  };

  const decrementarCantidad = (id) => {
    setCarritoItems((prev) =>
      prev.map((item) =>
        item.id === id && item.cantidad > 1
          ? { ...item, cantidad: item.cantidad - 1 }
          : item
      )
    );
  };

  const eliminarItem = (id) => {
    setCarritoItems((prev) => prev.filter((item) => item.id !== id));
  };

  const vaciarCarrito = () => {
    setCarritoItems([]);
  };

  const aplicarPreciosRecibidos = useCallback((productosActualizados) => {
    const itemsOrigen = carritoItemsRef.current;
    if (!itemsOrigen.length || !Array.isArray(productosActualizados)) {
      return { items: itemsOrigen, preciosActualizados: false, eliminados: [] };
    }

    const preciosPorId = new Map(
      productosActualizados.map((producto) => [String(producto.id || producto.productoId), producto])
    );

    const resultado = aplicarPreciosEnItems(itemsOrigen, preciosPorId);
    setCarritoItems(resultado.items);
    return resultado;
  }, []);

  const refrescarPrecios = useCallback(async (itemsBase) => {
    const itemsOrigen = itemsBase ?? carritoItemsRef.current;

    if (!itemsOrigen.length) {
      return { items: [], preciosActualizados: false, eliminados: [] };
    }

    try {
      const resultados = await Promise.all(
        itemsOrigen.map(async (item) => {
          try {
            const actual = await obtenerProductoPorId(item.id);
            return { item, actual };
          } catch (error) {
            console.error(`Error al obtener producto ${item.id} para refrescar precio:`, error);
            return { item, actual: null, error: true };
          }
        })
      );

      if (resultados.some((resultado) => resultado.error)) {
        return {
          items: itemsOrigen,
          preciosActualizados: false,
          eliminados: [],
          error: 'No se pudieron actualizar los precios',
        };
      }

      const preciosPorId = new Map(
        resultados
          .filter(({ actual }) => actual)
          .map(({ item, actual }) => [String(item.id), actual])
      );

      const resultado = aplicarPreciosEnItems(itemsOrigen, preciosPorId);
      setCarritoItems(resultado.items);
      return resultado;
    } catch (error) {
      console.error('Error al refrescar precios del carrito:', error);
      return {
        items: itemsOrigen,
        preciosActualizados: false,
        eliminados: [],
        error: error.message || 'No se pudieron actualizar los precios',
      };
    }
  }, []);

  const calcularTotales = (items) => {
    const cantidadTotalCalc = items.reduce((acc, item) => acc + item.cantidad, 0);
    const montoTotal = items.reduce(
      (acc, item) => acc + toNumber(item.precio) * item.cantidad,
      0
    );

    return {
      cantidadTotalCalc,
      montoTotal,
    };
  };

  const finalizarCompra = async (nombreContacto, telefonoContacto, metodoPago = 'mercadopago') => {
    try {
      if (!nombreContacto || !telefonoContacto || nombreContacto.trim() === '' || telefonoContacto.trim() === '') {
        return { success: false, error: 'Debe proporcionar nombre y teléfono de contacto' };
      }

      if (telefonoContacto.trim().length < 8) {
        return { success: false, error: 'El teléfono debe tener al menos 8 caracteres' };
      }

      const refresh = await refrescarPrecios();
      if (refresh.error) {
        return {
          success: false,
          error: 'No se pudieron verificar los precios actuales. Intentá nuevamente.',
        };
      }

      const itemsActualizados = refresh.items;
      if (!itemsActualizados.length) {
        return {
          success: false,
          error: refresh.eliminados.length
            ? 'Los productos de tu carrito ya no están disponibles.'
            : 'El carrito está vacío',
          preciosActualizados: refresh.preciosActualizados,
        };
      }

      if (refresh.eliminados.length > 0 || refresh.preciosActualizados) {
        return {
          success: false,
          preciosActualizados: true,
          error: refresh.eliminados.length
            ? 'Algunos productos ya no están disponibles o cambiaron de precio. Revisá el carrito e intentá de nuevo.'
            : 'Los precios de tu carrito se actualizaron. Revisá el nuevo total y volvé a confirmar la compra.',
        };
      }

      const productos = itemsActualizados.map((item) => ({
        id: item.id,
        nombre: item.nombre,
        precio: toNumber(item.precio),
        cantidad: item.cantidad,
      }));

      const { montoTotal } = calcularTotales(itemsActualizados);
      const metaCustomerType = META_CUSTOMER_TYPE;
      const metaEventId = generateMetaPurchaseEventId();
      const metaEventTime = generateMetaPurchaseEventTime();

      let respuesta;
      try {
        respuesta = await ApiService.post('/pedidos', {
          productos,
          montoTotal,
          descuento: 0,
          estado: 'CREADO',
          metodoPago,
          nombreContacto: nombreContacto.trim(),
          telefonoContacto: telefonoContacto.trim(),
          metaEventId,
          metaEventName: META_PURCHASE_EVENT_NAME,
          metaCustomerType,
          metaEventTime,
        });
      } catch (error) {
        if (error?.status === 409 && error?.payload?.code === 'PRECIOS_ACTUALIZADOS') {
          const data = error.payload.data || {};
          if (Array.isArray(data.productos)) {
            aplicarPreciosRecibidos(data.productos);
          } else {
            await refrescarPrecios();
          }
          return {
            success: false,
            preciosActualizados: true,
            error: error.payload.message
              || 'Los precios de tu carrito se actualizaron. Revisá el nuevo total y volvé a confirmar la compra.',
          };
        }
        throw error;
      }

      const initPoint = respuesta?.data?.init_point;
      const pedido = respuesta?.data?.pedido;
      const pedidoId = pedido?._id;
      const montoTotalPedido = toNumber(pedido?.montoNeto ?? pedido?.montoTotal ?? montoTotal);

      if (metodoPago === 'efectivo') {
        if (!pedidoId) {
          return {
            success: false,
            error: 'No se recibió la confirmación del pedido. Intentá nuevamente.',
          };
        }

        return {
          success: true,
          metodoPago: 'efectivo',
          pedidoId,
          montoTotal: montoTotalPedido,
          whatsappLink: respuesta?.data?.whatsappLink,
          metaEventId,
          metaEventName: META_PURCHASE_EVENT_NAME,
          metaCustomerType,
          metaEventTime,
        };
      }

      if (!initPoint) {
        return {
          success: false,
          error: 'No se recibió el enlace de pago de Mercado Pago. Intentá nuevamente.',
        };
      }

      setMpCheckoutPedidoId(pedidoId);
      window.location.href = initPoint;

      return {
        success: true,
        redirecting: true,
        pedidoId,
        metaEventId,
        metaEventName: META_PURCHASE_EVENT_NAME,
        metaCustomerType,
        metaEventTime,
      };
    } catch (error) {
      console.error('Error al finalizar compra:', error);

      let mensajeError = 'Error al procesar la compra';
      let stockErrors = null;

      if (error?.payload) {
        mensajeError = error.payload.message || error.payload.error || error.payload.msg || JSON.stringify(error.payload);

        if (error.payload.details && Array.isArray(error.payload.details)) {
          stockErrors = {};
          error.payload.details.forEach((detail) => {
            if (detail.productId && detail.error) {
              stockErrors[detail.productId] = detail.error;
            }
          });
        }
      } else if (error?.message) {
        mensajeError = error.message;
      }

      return { success: false, error: mensajeError, stockErrors };
    }
  };

  const cantidadTotal = carritoItems.reduce((total, item) => total + item.cantidad, 0);
  const total = carritoItems.reduce(
    (acc, item) => acc + toNumber(item.precio) * item.cantidad,
    0
  );

  return (
    <CarritoContext.Provider
      value={{
        carritoItems,
        agregarAlCarrito,
        incrementarCantidad,
        decrementarCantidad,
        eliminarItem,
        vaciarCarrito,
        refrescarPrecios,
        finalizarCompra,
        cantidadTotal,
        total,
        showCarritoSidebar,
        setShowCarritoSidebar,
      }}
    >
      {children}
    </CarritoContext.Provider>
  );
};
